import { LocalDateType } from '@app/models';
import { createCalendar } from '@app/pcs/models/calendar.schema';
import { PapRequestStatus } from '@app/pcs/models/pap-request-status';
import { ValidityPeriod, validityPeriodDetailed } from '@app/pcs/models/validity-period';
import { hasValue } from '@app/util';
import {gql} from 'apollo-angular';
import { schema } from 'normalizr';
import * as R from 'ramda';

export interface WeekDay {
  name: string;
  dayValue: number;
  selected?: boolean;
}

export interface PapCalendarDay {
  reserved: boolean;
  requested: boolean;
  available: boolean;
}

export interface UsedPapCalendarDay {
  available?: boolean;
  papRequestStatus?: PapRequestStatus;
}

export interface CompareStatus {
  added: boolean;
  removed: boolean;
}

export interface CalendarDay {
  name: string;
  dayValue: number;
  index: number;
  monthIndex: number;
  selected?: boolean;
  selectable?: boolean;
  editable?: boolean;
  incoming?: boolean;
  warning?: boolean;
  extra?: boolean;
  out?: boolean;
  usedPapCalendarDay?: UsedPapCalendarDay;
  papCalendarDay?: PapCalendarDay;
  compareStatus?: CompareStatus;
}

export interface CalendarMonth {
  isFirstMonth: boolean;
  year: number;
  monthValue: number;
  index: number;
  numberOfDays: number;
  name: string;
  startIndex: number;
  endIndex: number;
  placeholderDays: CalendarDay[];
  days: CalendarDay[];
}

export interface Calendar {
  id: number; // this field actually is targetId
  firstDate: string;
  lastDate: string;
  numberOfMonths: number;
  weekdays: any[];
  months: CalendarMonth[];
  ttpFirstDate: any;
  ttpLastDate: any;
  validityPeriod: ValidityPeriod;
  totalDays: number;
  ttpId: number;
  calendarWatermark?: CalendarWatermark;
  calId?: number;
}

export interface CalendarWatermark {
  startDate: LocalDateType;
  endDate: LocalDateType;
  weekDays: CalendarRunningDay[];
  monthDays: CalendarRunningDay[];
  runningMonths: boolean[];
  text: string;
  cancelledDays: number[][];
  additionalDays: number[][];
  selectedWeekDays?: boolean[];
  selectedMonthDays?: boolean[];
  selectedMonthPercentage?: number[];
  totalRunningDays?: number;
}

export interface CalendarRunningDay {
  day: CalendarDay;
  percentage: number;
}

export interface CalendarEntities {
  [key: number]: Calendar;
}

export interface CalendarNormalized {
  entities: {
    calendar: Calendar;
  };
  result: number;
}

export const calendarSchema = new schema.Entity('calendar', {}, {
  idAttribute: 'targetId',
  processStrategy: (value) => createCalendar(value)
});

export const calendarDayDetailed = gql`
  fragment calendarDayDetailed on CalendarDay {
    selected
    selectable
    editable
    incoming
    warning
    extra
    usedPapCalendarDay {
      available
      papRequestStatus {
        status
        category
      }
    }
    papCalendarDay {
      reserved
      requested
      available
    }
  }
`;

export const calendarCompareDay = gql`
  fragment calendarCompareDay on CalendarDay {
    selected
    selectable
    editable
    compareStatus {
      added
      removed
    }
  }
`;

export const calendarWatermarkDetailedFragment = gql`
  fragment calendarWatermarkDetailed on CalendarWatermark {
    weekDays {
      percentage
      day {
        ...calendarDayDetailed
      }
    }
    monthDays {
      percentage
      day {
        ...calendarDayDetailed
      }
    }
    additionalDays
    cancelledDays
    startDate
    endDate
    totalRunningDays
  }
  ${calendarDayDetailed}
`;

export const calendarDetailed = gql`
  fragment calendarDetailed on Calendar {
    id
    targetId
    ttpId
    totalDays
    ttpFirstDate
    ttpLastDate
    validityPeriod {
      ...validityPeriodDetailed
    }
    days {
      ...calendarDayDetailed
    }
    calendarWatermark {
      ...calendarWatermarkDetailed
    }
  }
  ${validityPeriodDetailed}
  ${calendarDayDetailed}
  ${calendarWatermarkDetailedFragment}
`;

export const calendarCompare = gql`
    fragment calendarCompare on Calendar {
      id
      targetId
      totalDays
      ttpFirstDate
      ttpLastDate
      days {
        ...calendarCompareDay
      }
      calendarWatermark {
        weekDays {
          day {
            selected
          }
        }
      }
    }
  ${calendarCompareDay}
`;

export const calendarWatermarkOnlyWithSelected = gql`
  fragment calendarWatermarkOnlyWithSelected on CalendarWatermark {
    selectedWeekDays
    selectedMonthDays
    selectedMonthPercentage
  }
`;

export type DayPredicate = (d: CalendarDay) => boolean;
export type MonthPredicate = (m: CalendarMonth) => boolean;
export type CalendarPredicate = (c: Calendar) => boolean;

const isAvailable: DayPredicate = R.compose(
  R.propOr(false, 'available'),
  <any> R.propOr({}, 'usedPapCalendarDay')
);

export const isNotAvailable: DayPredicate = R.ifElse(
  day => hasValue(day.usedPapCalendarDay),
  day => !day.usedPapCalendarDay.available,
  R.always(false)
);
export const isSelected: DayPredicate = R.prop('selected');

type GetMonths = (c: Calendar) => CalendarMonth[];
export const getMonths = <GetMonths> R.prop('months');
type GetDays = (m: CalendarMonth) => CalendarDay[];
export const getDays = <GetDays> R.prop('days');
